import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { UserModel as UserType } from '@/types'
import { UserFormState } from './userFieldsConfig'
import { useUserForm } from './useUserForm'

import { newStaffCard } from './userFieldsConfig'
import { UserProfileTab } from './UserProfileTab'
import { UserPermissionsTab } from './UserPermissionsTab'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface UserDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editUser: UserType.User | null
  closeDialog: () => void
  onCreate: (data: UserFormState) => void
  onUpdate: (id: string, data: UserFormState) => void
  loading?: boolean
}

export const UserDialog = ({
  open,
  mode,
  editUser,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: UserDialogProps) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    errors,
    processing,
    galleryOpen,
    setGalleryOpen,
    rolesLoading,
    roleOptions,
    fields,
    updatePermission,
    getPermissionState,
    isAllowedByRole,
    onSubmit,
  } = useUserForm({ open, mode, editUser, onCreate, onUpdate })

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  return (
    <>
      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent size="lg" className="flex h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:h-[90vh]">
          <DialogHeader className="pb-4">
            <DialogTitle>
              {mode === 'create'
                ? 'Add staff'
                : mode === 'edit'
                  ? `Edit ${editUser?.name}`
                  : 'Staff details'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'view'
                ? 'Profile, roles and permissions for this staff account.'
                : 'Set the profile, roles and permissions for this staff account.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="profile" className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-border px-6 pb-3">
              <TabsList>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="permissions">Permissions</TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-6">
                <TabsContent value="profile" className="mt-0 outline-none">
                  <UserProfileTab
                    mode={mode}
                    control={control}
                    watch={watch}
                    errors={errors}
                    fields={fields}
                    roleOptions={roleOptions}
                    rolesLoading={rolesLoading}
                    onSelectImage={() => setGalleryOpen(true)}
                    onRemoveImage={() => {
                      setValue('image_url_temp', '')
                      setValue('image_id', '')
                    }}
                    onRoleChange={(values) => setValue('role_ids', values, { shouldValidate: true })}
                    onNewCard={() => setValue('barcode', newStaffCard(), { shouldDirty: true })}
                  />
                </TabsContent>

                <TabsContent value="permissions" className="mt-0 outline-none">
                  <UserPermissionsTab
                    mode={mode}
                    getPermissionState={getPermissionState}
                    isAllowedByRole={isAllowedByRole}
                    updatePermission={updatePermission}
                  />
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeDialog}
              disabled={loading}>
              {mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button
                type="button"
                disabled={loading || processing}
                onClick={handleSubmit(onSubmit)}>
                {(loading || processing) && <Spinner />}
                {mode === 'create' ? 'Add staff' : 'Save'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaGalleryModal
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelect={(files) => {
          if (files.length > 0) {
            setValue('image_url_temp', files[0].url)
            setValue('image_id', files[0].id)
          }
        }}
      />
    </>
  )
}
